/**
 * Firebase Cloud Messaging (web) — optional; returns null when env is not configured.
 */

const firebaseConfig = () => {
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY?.trim();
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID?.trim();
  const messagingSenderId = process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID?.trim();
  const appId = process.env.REACT_APP_FIREBASE_APP_ID?.trim();
  if (!apiKey || !projectId || !messagingSenderId || !appId) return null;
  return {
    apiKey,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN?.trim() || `${projectId}.firebaseapp.com`,
    projectId,
    messagingSenderId,
    appId,
  };
};

let messagingPromise = null;
let swRegistrationPromise = null;

const getServiceWorkerRegistration = async (config) => {
  if (swRegistrationPromise) return swRegistrationPromise;
  swRegistrationPromise = (async () => {
    const swCode = `
      importScripts('https://www.gstatic.com/firebasejs/10.14.1/firebase-app-compat.js');
      importScripts('https://www.gstatic.com/firebasejs/10.14.1/firebase-messaging-compat.js');
      firebase.initializeApp(${JSON.stringify(config)});
      const messaging = firebase.messaging();
      messaging.onBackgroundMessage((payload) => {
        const title = payload.notification?.title || 'PendPay';
        self.registration.showNotification(title, {
          body: payload.notification?.body || '',
          icon: '/favicon.ico',
          data: payload.data || {},
        });
      });
      self.addEventListener('notificationclick', (event) => {
        event.notification.close();
        event.waitUntil(clients.openWindow('/'));
      });
    `;
    const blob = new Blob([swCode], { type: "application/javascript" });
    const url = URL.createObjectURL(blob);
    return navigator.serviceWorker.register(url);
  })();
  return swRegistrationPromise;
};

const loadMessaging = async () => {
  if (messagingPromise) return messagingPromise;
  messagingPromise = (async () => {
    const config = firebaseConfig();
    if (!config) return null;
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return null;

    const { initializeApp } = await import("firebase/app");
    const { getMessaging, getToken, onMessage, isSupported } = await import("firebase/messaging");

    const supported = await isSupported();
    if (!supported) return null;

    const app = initializeApp(config);
    const messaging = getMessaging(app);
    return { messaging, getToken, onMessage, config };
  })().catch(() => null);

  return messagingPromise;
};

export const isPushConfigured = () => !!firebaseConfig();

export const registerPushToken = async () => {
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim();
  const loaded = await loadMessaging();
  if (!loaded || !vapidKey) return null;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  const registration = await getServiceWorkerRegistration(loaded.config);
  const token = await loaded.getToken(loaded.messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  return token || null;
};

export const onForegroundPush = async (callback) => {
  const loaded = await loadMessaging();
  if (!loaded) return () => {};
  return loaded.onMessage(loaded.messaging, callback);
};
